import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { useConfirm } from "../lib/confirm";
import { useToast } from "../lib/toast";
import { BarcodeScannerSheet } from "./BarcodeScannerSheet";
import { SheetShell } from "./SheetShell";
import { Paywall } from "./Paywall";
import { PremiumFeatureIntro } from "./PremiumFeatureIntro";
import { LockedField } from "./ProLock";
import { UnitInput } from "./ui/UnitInput";
import { useUnits } from "../lib/useUnits";
import { kcalToKj, kjToKcal } from "@shared/units";
import { Camera } from "lucide-react";
import { Mascot } from "./Mascot";
import { Button } from "./ui/Button";
import { BarcodeProductPreview } from "./BarcodeProductPreview";
import { BarcodeIcon } from "./BarcodeIcon";
import { MealSlotPicker } from "./MealSlotPicker";
import { canScanBarcode, normalizeCode, derivePer100g } from "../lib/barcode";
import { defaultMealSlot } from "../lib/date";
import { downscaleToJpegBase64 } from "../lib/image";
import { hapticSuccess, hapticWarning, hapticSelect } from "../lib/haptics";
import { useEntitlement } from "../lib/useEntitlement";
import { sumItems, scaleEstimateItem } from "@shared/estimate";
import type { FoodDraft } from "../lib/foodDraft";
import type { FoodEntry, MealSlot, EstimateResponse, EstimateItem, NormalizedProduct } from "@shared/types";
import { MicrosList } from "./MicrosList";
import { WeightStepper } from "./WeightStepper";

type Mode = "add" | "edit";

interface Props {
  open: boolean;
  mode: Mode;
  initial: Partial<FoodEntry>;
  context: {
    eaten_on: string;
    source: "plan" | "custom" | "quick";
    plan_meal_slot: MealSlot | null;
  };
  /** When the sheet is opened from a continuous-scan miss, seed the barcode so
   *  the learning-cache write-back on save remembers this product. */
  scannedCode?: string | null;
  /** Capture-first photo flow: the photo was already picked BEFORE this sheet
   *  opened (native picker with no UI underneath) — analyze it on open. */
  initialPhoto?: File | null;
  /** Voice flow hands the finished estimate + transcript straight in. */
  initialEstimate?: { response: EstimateResponse; transcript?: string } | null;
  /** Persist the finished draft. The sheet does not write — the opener decides
   *  whether to await the network (and show a failure here) or to apply the
   *  draft locally and write behind the scenes. Throwing keeps the sheet open
   *  with the message shown. */
  onSaved: (draft: FoodDraft) => Promise<void>;
  /** Remove the entry being edited. Only edit mode shows the control, and only
   *  Today opens the sheet in edit mode. */
  onDeleted?: (id: number) => Promise<void>;
  onClose: () => void;
}

export function FoodSheet({ open, mode, initial, context, scannedCode: seededCode, initialPhoto = null, initialEstimate = null, onSaved, onDeleted, onClose }: Props) {
  const t = useT();
  const confirm = useConfirm();
  const toast = useToast();
  const [description, setDescription] = useState("");
  const [items, setItems] = useState<EstimateItem[]>([]);
  // Pristine AI items (index-aligned with `items`) — weight edits always scale
  // from these, never from an already-rounded intermediate, so repeated/partial
  // edits can't progressively round small macros and micros down to zero.
  const [originalItems, setOriginalItems] = useState<EstimateItem[]>([]);
  const [name, setName] = useState("");
  const [calories, setCalories] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [sugar, setSugar] = useState("");
  const [fiber, setFiber] = useState("");
  const [micros, setMicros] = useState<Record<string, number> | null>(null);
  const [busy, setBusy] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);
  // The scanned photo, kept on screen (a local object URL — the image itself
  // is never uploaded anywhere but the estimator and never stored). While the
  // model works, a scan line sweeps it; when items land they stagger in.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [revealSeq, setRevealSeq] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [scanIntro, setScanIntro] = useState(false);
  const [saveAsMeal, setSaveAsMeal] = useState(false);
  // What the AI said, before any user edits — drives the range line, the
  // "you usually log this as…" chip, and the correction write-back on save.
  const [aiMeta, setAiMeta] = useState<{
    calories: number; nameKey: string;
    band: { low: number; high: number } | null;
    prior: NonNullable<EstimateResponse["user_prior"]> | null;
  } | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [product, setProduct] = useState<NormalizedProduct | null>(null);
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [amountG, setAmountG] = useState("100");
  const [amountUnit, setAmountUnit] = useState<"g" | "ml">("g");
  const [slot, setSlot] = useState<MealSlot>("breakfast");
  const { premium } = useEntitlement();
  const units = useUnits();
  const kj = units.prefs.energy === "kj";
  const canScan = canScanBarcode();
  const descRef = useRef<HTMLTextAreaElement | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setDescription("");
    setPhotoUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
    setRevealSeq(false);
    setItems([]);
    setOriginalItems([]);
    setName(initial.name ?? "");
    setCalories(initial.calories !== undefined && initial.calories !== null ? String(initial.calories) : "");
    setProtein(initial.protein_g !== null && initial.protein_g !== undefined ? String(initial.protein_g) : "");
    setCarbs(initial.carbs_g !== null && initial.carbs_g !== undefined ? String(initial.carbs_g) : "");
    setFat(initial.fat_g !== null && initial.fat_g !== undefined ? String(initial.fat_g) : "");
    setSugar(initial.sugar_g !== null && initial.sugar_g !== undefined ? String(initial.sugar_g) : "");
    setFiber(initial.fiber_g !== null && initial.fiber_g !== undefined ? String(initial.fiber_g) : "");
    setMicros(initial.micros ?? null);
    setErr(null);
    setSaveAsMeal(false);
    setAiMeta(null);
    setProduct(null);
    setScannedCode(seededCode ?? null);
    setAmountG("100");
    setAmountUnit("g");
    // Pre-select the meal by the entry's slot (edit) or by time of day (add).
    setSlot(context.plan_meal_slot ?? defaultMealSlot(new Date().getHours()));
  }, [open, initial, seededCode, context.plan_meal_slot]);

  // An estimate takes seconds, and the form stays usable throughout — so
  // someone can be typing their own portion when the answer lands. Whatever
  // they touched while the request was in flight is theirs and survives: a
  // number the user entered, silently replaced by the AI's, is exactly the kind
  // of wrong calorie count nobody thinks to double-check.
  const inFlight = useRef(false);
  const typedWhileWaiting = useRef(new Set<string>());
  function guard(key: string, set: (v: string) => void) {
    return (v: string) => {
      if (inFlight.current) typedWhileWaiting.current.add(key);
      set(v);
    };
  }
  function beginEstimate() {
    inFlight.current = true;
    typedWhileWaiting.current.clear();
  }
  function endEstimate() {
    inFlight.current = false;
  }

  function fillFromEstimate(r: EstimateResponse, fallbackName: string) {
    const mine = typedWhileWaiting.current;
    const fill = (key: string, set: (v: string) => void, v: string) => {
      if (!mine.has(key)) set(v);
    };
    setAiMeta({
      calories: r.calories,
      nameKey: fallbackName,
      band: r.kcal_low !== undefined && r.kcal_high !== undefined ? { low: r.kcal_low, high: r.kcal_high } : null,
      prior: r.user_prior && Math.abs(r.user_prior.calories - r.calories) / Math.max(1, r.calories) > 0.1
        ? r.user_prior : null,
    });
    setItems(r.items);
    setOriginalItems(r.items);
    if (!name && !mine.has("name")) setName(fallbackName.slice(0, 40));
    fill("calories", setCalories, String(r.calories));
    fill("protein", setProtein, String(r.protein_g));
    fill("carbs", setCarbs, String(r.carbs_g));
    fill("fat", setFat, String(r.fat_g));
    fill("sugar", setSugar, r.sugar_g !== null && r.sugar_g !== undefined ? String(r.sugar_g) : "");
    fill("fiber", setFiber, r.fiber_g !== null && r.fiber_g !== undefined ? String(r.fiber_g) : "");
    setMicros(r.micros ?? null);
  }

  // Voice flow: seed the finished estimate exactly once per open.
  const seededEstimateRef = useRef<object | null>(null);
  useEffect(() => {
    if (open && initialEstimate && seededEstimateRef.current !== initialEstimate) {
      seededEstimateRef.current = initialEstimate;
      if (initialEstimate.transcript) setDescription(initialEstimate.transcript);
      fillFromEstimate(initialEstimate.response,
        initialEstimate.transcript ?? initialEstimate.response.items[0]?.name ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialEstimate]);

  // Analyze a photo handed in from the capture-first flow exactly once per file.
  const analyzedPhotoRef = useRef<File | null>(null);
  useEffect(() => {
    if (open && initialPhoto && analyzedPhotoRef.current !== initialPhoto) {
      analyzedPhotoRef.current = initialPhoto;
      void analyzePhoto(initialPhoto);
    }
  }, [open, initialPhoto]);

  if (!open) return null;

  // Known error keys get their translated copy; anything else (including raw
  // provider internals like "estimator unavailable — openai: 401") shows the
  // generic failure line instead of leaking English internals into the UI.
  function estimateErrText(e: unknown, generic: string): string {
    if (!(e instanceof ApiError)) return generic;
    if (e.message === "rate_limited") return t("error.rate_limited");
    if (e.message === "ai_quota_exceeded") return t("error.ai_quota");
    return generic;
  }

  async function estimate() {
    if (!description.trim()) return;
    setEstimating(true); setErr(null); beginEstimate();
    try {
      const r: EstimateResponse = await api.estimateFood(description.trim());
      fillFromEstimate(r, description.trim());
    } catch (e) {
      setErr(estimateErrText(e, t("food_sheet.ai_failed")));
    } finally {
      endEstimate();
      setEstimating(false);
    }
  }

  async function analyzePhoto(file: File) {
    setPhotoUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(file); });
    setRevealSeq(false);
    setAnalyzingPhoto(true); setErr(null); beginEstimate();
    try {
      const b64 = await downscaleToJpegBase64(file);
      const r: EstimateResponse = await api.estimatePhoto(b64, description.trim() || undefined);
      setRevealSeq(true); // stagger the found items into view
      fillFromEstimate(r, r.items[0]?.name ?? "");
    } catch (e) {
      if (e instanceof ApiError && e.message === "premium_required") { setShowPaywall(true); }
      else setErr(estimateErrText(e, t("food_sheet.photo_failed")));
    } finally {
      endEstimate();
      setAnalyzingPhoto(false);
    }
  }

  // Edit/delete a detected item, then recompute the totals from the remaining items.
  function recomputeTotals(next: EstimateItem[]) {
    setItems(next);
    if (!next.length) {
      setCalories(""); setProtein(""); setCarbs(""); setFat(""); setSugar(""); setMicros(null);
      return;
    }
    const s = sumItems(next);
    setCalories(String(s.calories));
    if (s.protein_g !== null) setProtein(String(s.protein_g));
    if (s.carbs_g !== null) setCarbs(String(s.carbs_g));
    if (s.fat_g !== null) setFat(String(s.fat_g));
    if (s.sugar_g !== null) setSugar(String(s.sugar_g));
    setMicros(s.micros);
  }

  // Adjust one item's weight (and unit), rescaling its nutrients from the PRISTINE
  // base item (originalItems[i]) so repeated edits don't accumulate rounding error,
  // then re-sum. Falls back to the current item if the base is somehow missing.
  function setItem(i: number, grams: number, unit: "g" | "ml") {
    recomputeTotals(items.map((x, j) =>
      j === i ? scaleEstimateItem({ ...(originalItems[j] ?? x), unit }, grams) : x));
  }

  // Remove an item, keeping items and originalItems index-aligned, then re-sum.
  function removeItem(i: number) {
    setOriginalItems(originalItems.filter((_, j) => j !== i));
    recomputeTotals(items.filter((_, j) => j !== i));
  }

  async function save() {
    const isNewProduct = !!scannedCode && !product && mode === "add";
    const amtVal = Math.round(Number(amountG));
    const amountOk = !isNewProduct
      || (amountG.trim() !== "" && Number.isFinite(amtVal) && amtVal >= 1 && amtVal <= 2000);
    if (!name.trim() || !calories || !amountOk) { setErr(t("food_sheet.required")); return; }
    setBusy(true); setErr(null);
    try {
      const draft: FoodDraft = {
        ...(mode === "edit" && initial.id !== undefined ? { id: initial.id } : {}),
        eaten_on: context.eaten_on,
        source: context.source,
        plan_meal_slot: slot,
        name: name.trim(),
        calories: Math.round(Number(calories)),
        // Detail amounts are Pro: a free add logs the bare entry, a free edit
        // omits the fields entirely so the stored values persist.
        ...(premium
          ? {
              protein_g: protein ? Number(protein) : null,
              carbs_g: carbs ? Number(carbs) : null,
              fat_g: fat ? Number(fat) : null,
              sugar_g: sugar ? Number(sugar) : null,
              fiber_g: fiber ? Number(fiber) : null,
              micros: micros,
            }
          : mode === "add"
            ? { protein_g: null, carbs_g: null, fat_g: null, sugar_g: null, fiber_g: null, micros: null }
            : {}),
      };
      // The opener persists it. Anything below only runs once the log is
      // accepted — the learning caches must never remember a meal that was
      // rejected.
      await onSaved(draft);

      const raw = scannedCode ?? product?.code ?? null;
      const code = raw ? normalizeCode(raw) : null;
      if (code) api.barcodeRecordRecent(code).catch(() => { /* ignore */ });
      // Learning cache: a scanned product OFF didn't have → remember it under the
      // barcode so the next scan resolves instantly. Best-effort; never blocks the log.
      if (code && !product && mode === "add") {
        const amt = Math.round(Number(amountG));
        if (Number.isFinite(amt) && amt >= 1) {
          const learned: NormalizedProduct = {
            code,
            name: name.trim(),
            brand: "",
            image_url: null,
            unit: amountUnit,
            serving_size_g: amt,
            per_100g: derivePer100g(
              {
                calories: Math.round(Number(calories)),
                protein_g: protein ? Number(protein) : null,
                carbs_g: carbs ? Number(carbs) : null,
                fat_g: fat ? Number(fat) : null,
                sugar_g: sugar ? Number(sugar) : null,
              },
              amt,
            ),
          };
          api.barcodeCachePut(code, learned).catch(() => { /* best-effort */ });
        }
      }
      // Correction memory: the user materially disagreed with the AI — remember
      // their number for this food (best-effort, mirrors the barcode learning).
      if (aiMeta && mode === "add") {
        const savedKcal = Math.round(Number(calories));
        if (Number.isFinite(savedKcal) && aiMeta.calories > 0 &&
            Math.abs(savedKcal - aiMeta.calories) / aiMeta.calories > 0.1) {
          api.aiCorrectionPut(aiMeta.nameKey || name.trim(), {
            calories: savedKcal,
            protein_g: protein ? Number(protein) : null,
            carbs_g: carbs ? Number(carbs) : null,
            fat_g: fat ? Number(fat) : null,
            sugar_g: sugar ? Number(sugar) : null,
          }).catch(() => { /* best-effort */ });
        }
      }

      // "Save as meal": snapshot the detected items so this exact combination
      // re-logs in one tap later. Best-effort — never blocks the log; a 402
      // (free tier) opens the paywall instead of failing silently.
      if (saveAsMeal && name.trim() && calories) {
        // With AI items, snapshot the full breakdown; a barcode scan or a
        // manual entry becomes a one-item meal from the form itself — every
        // add path can now be kept (owner: saving was unreachable after
        // scanning).
        const mealItems = items.length > 0
          ? items.map((it) => ({
              name: it.name, kcal: it.kcal,
              protein_g: it.protein_g ?? null, carbs_g: it.carbs_g ?? null,
              fat_g: it.fat_g ?? null, sugar_g: it.sugar_g ?? null,
              micros_json: it.micros ? JSON.stringify(it.micros) : null,
              grams: it.grams ?? null, unit: it.unit ?? null,
            }))
          : [{
              name: name.trim(), kcal: Math.round(Number(calories)),
              protein_g: protein ? Number(protein) : null,
              carbs_g: carbs ? Number(carbs) : null,
              fat_g: fat ? Number(fat) : null,
              sugar_g: sugar ? Number(sugar) : null,
              micros_json: micros ? JSON.stringify(micros) : null,
              grams: scannedCode || product ? Math.round(Number(amountG)) || null : null,
              unit: amountUnit ?? null,
            }];
        api.createSavedMeal(name.trim(), null, mealItems)
          .then(() => toast.success(t("saved.saved_toast")))
          .catch((e) => {
            if (e instanceof ApiError && e.message === "premium_required") setShowPaywall(true);
          });
      }
      hapticSuccess();
      toast.success(mode === "edit" ? t("toast.saved") : t("toast.logged"));
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t("food_sheet.save_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (mode !== "edit" || initial.id === undefined || !onDeleted) return;
    if (!(await confirm({ message: t("confirm.delete_entry"), danger: true, confirmLabel: t("common.delete") }))) return;
    setBusy(true);
    try { await onDeleted(initial.id); hapticWarning(); toast.success(t("toast.deleted")); onClose(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  const title = mode === "add" ? t("food_sheet.add_meal") : t("food_sheet.edit_meal");

  return (
    <SheetShell onClose={onClose} variant="full">
        <div className="flex items-center justify-between mt-4 mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          {mode === "add" && (
            <div className="flex items-center gap-1">
              <input
                ref={photoInputRef}
                type="file" accept="image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) analyzePhoto(f); e.target.value = ""; }}
              />
              <button
                type="button"
                onClick={() => { if (!premium) { setScanIntro(true); return; } photoInputRef.current?.click(); }}
                disabled={estimating || analyzingPhoto}
                aria-label={t("food_sheet.photo")}
                className="p-3 -m-1 text-fg-secondary press text-2xl leading-none disabled:opacity-50"
              ><Camera size={20} aria-hidden="true" /></button>
              {canScan && (
                <button
                  onClick={() => setScannerOpen(true)}
                  aria-label={t("barcode.open_scanner")}
                  className="p-3 -m-1 text-fg-secondary"
                >
                  <BarcodeIcon className="w-6 h-6" />
                </button>
              )}
            </div>
          )}
        </div>

        <div className="mb-4">
          <div className="text-sm text-fg-muted uppercase tracking-wider mb-1.5">{t("food_sheet.meal")}</div>
          <MealSlotPicker value={slot} onChange={setSlot} />
        </div>

        {product && (
          <BarcodeProductPreview
            product={product}
            onChange={(_amt, macros, entryName) => {
              // WeightStepper always emits a clamped amount ≥ 1, so the derived
              // macros are always valid; the save() amountOk guard is the gate.
              setName(entryName);
              setCalories(String(macros.calories));
              setProtein(macros.protein_g === null ? "" : String(macros.protein_g));
              setCarbs(macros.carbs_g === null ? "" : String(macros.carbs_g));
              setFat(macros.fat_g === null ? "" : String(macros.fat_g));
              setSugar(macros.sugar_g === null ? "" : String(macros.sugar_g));
              setMicros(macros.micros);
            }}
            onClear={() => {
              // Tapping ✕ on the preview must also reset the derived form fields,
              // otherwise the user is left with a "blank" preview but a populated
              // (and confusing) name/calories/macros set from the scanned product.
              setProduct(null);
              setScannedCode(null);
              setName("");
              setCalories("");
              setProtein("");
              setCarbs("");
              setFat("");
              setSugar(""); setMicros(null);
            }}
          />
        )}

        {scannedCode && !product && (
          <div className="mb-3 px-3 py-2 bg-warn/10 border border-warn/30 rounded-xl">
            <p className="text-sm text-warn/90">{t("barcode.new_product_hint")}</p>
            <label className="flex items-center gap-2 mt-2">
              <span className="text-sm text-fg-muted">{t("barcode.amount_eaten")}</span>
              <WeightStepper
                value={Math.max(1, Math.round(Number(amountG) || 100))}
                unit={amountUnit}
                onChange={(g, u) => { setAmountG(String(g)); setAmountUnit(u); }}
                max={2000}
                aria-label={t("barcode.amount_label")}
              />
            </label>
          </div>
        )}

        <label className="block mb-3">
          <span className="text-sm text-fg-muted uppercase tracking-wider">{t("food_sheet.describe")}</span>
          {photoUrl && (
            <div className="relative mb-3 rounded-2xl overflow-hidden border border-line">
              <img src={photoUrl} alt="" className="w-full max-h-52 object-cover" />
              {analyzingPhoto ? (
                <>
                  <div className="absolute inset-0 bg-black/25" />
                  <div className="scan-line" aria-hidden="true" />
                  <div className="absolute bottom-2 inset-x-0 flex items-center justify-center gap-2">
                    <Mascot pose="thinking" size={26} />
                    <span className="text-sm font-medium text-white drop-shadow">{t("scan.working")}</span>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setPhotoUrl((old) => { if (old) URL.revokeObjectURL(old); return null; })}
                  aria-label={t("common.dismiss")}
                  className="absolute top-1 right-1 w-11 h-11 rounded-full flex items-center justify-center text-white/90 bg-black/35 press"
                >✕</button>
              )}
            </div>
          )}
          {analyzingPhoto && (
            <div className="mb-3 space-y-2" aria-hidden="true">
              <div className="h-10 rounded-xl bg-surface animate-pulse" />
              <div className="h-10 rounded-xl bg-surface animate-pulse [animation-delay:150ms]" />
              <div className="h-10 rounded-xl bg-surface animate-pulse [animation-delay:300ms]" />
            </div>
          )}
          <textarea
            ref={descRef}
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            placeholder={t("food_sheet.describe_placeholder")}
            rows={2}
            className="w-full mt-1 px-3 py-2 bg-surface rounded-xl text-fg text-sm resize-none"
          />
        </label>
        <button
          onClick={estimate}
          disabled={estimating || analyzingPhoto || !description.trim()}
          className="w-full mb-4 py-2.5 bg-accent-move/20 text-accent-move rounded-full font-medium text-sm disabled:opacity-50"
        >
          {analyzingPhoto ? t("food_sheet.analyzing_photo") : estimating ? t("food_sheet.estimating") : t("food_sheet.estimate_ai")}
        </button>

        {items.length > 0 && (
          <div className="mb-4 bg-surface rounded-xl divide-y divide-line overflow-hidden">
            {items.map((it, i) => (
              <div
                key={i}
                className={`px-3 py-2 text-sm ${revealSeq ? "animate-rise" : ""}`}
                style={revealSeq ? { animationDelay: `${Math.min(i, 6) * 220}ms`, animationFillMode: "backwards" } : undefined}
              >
                <div className="flex items-center gap-2">
                  <span className="flex-1 truncate text-fg-secondary">{it.name}</span>
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    aria-label={t("food_sheet.remove_item")}
                    className="text-fg-faint px-3 py-2 -my-2 -mr-2 press"
                  >✕</button>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  {it.grams != null ? (
                    <>
                      <WeightStepper
                        value={it.grams}
                        unit={it.unit ?? "g"}
                        onChange={(g, u) => setItem(i, g, u)}
                        aria-label={it.name}
                      />
                      <span className="ml-auto tabular-nums text-fg-secondary">{it.kcal} kcal</span>
                    </>
                  ) : (
                    <>
                      <input
                        type="number" min={0} inputMode="numeric"
                        value={it.kcal}
                        onChange={(e) => recomputeTotals(items.map((x, j) =>
                          j === i ? { ...x, kcal: Math.max(0, Math.round(Number(e.target.value) || 0)) } : x))}
                        className="w-16 bg-surface-strong rounded-lg px-2 py-1 text-right tabular-nums"
                        aria-label={it.name}
                      />
                      <span className="text-fg-faint">kcal</span>
                    </>
                  )}
                </div>
                {/* The ripple made visible: these numbers rescale live with
                    the stepper, so adjusting grams shows its effect at once. */}
                {(it.protein_g != null || it.carbs_g != null || it.fat_g != null) && (
                  <div className="mt-1 text-sm text-fg-faint tabular-nums">
                    {it.protein_g != null && <span>{t("macro.p")} {it.protein_g}g</span>}
                    {it.carbs_g != null && <span> · {t("macro.c")} {it.carbs_g}g</span>}
                    {it.fat_g != null && <span> · {t("macro.f")} {it.fat_g}g</span>}
                  </div>
                )}
              </div>
            ))}
            <div className="px-3 py-2 flex justify-between text-sm text-fg-muted">
              <span>{t("food_sheet.items_total")}</span>
              <span className="tabular-nums">{sumItems(items).calories} kcal</span>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {aiMeta?.band && String(aiMeta.calories) === calories && (
            <p className="col-span-2 text-sm text-fg-muted">
              {t("estimate.range", {
                kcal: String(units.energy(aiMeta.calories).value),
                unit: units.energy(0).unit,
                low: String(units.energy(aiMeta.band.low).value),
                high: String(units.energy(aiMeta.band.high).value),
              })}
            </p>
          )}
          {aiMeta?.prior && (
            <button
              type="button"
              onClick={() => {
                const p2 = aiMeta.prior!;
                setCalories(String(p2.calories));
                if (p2.protein_g !== null) setProtein(String(p2.protein_g));
                if (p2.carbs_g !== null) setCarbs(String(p2.carbs_g));
                if (p2.fat_g !== null) setFat(String(p2.fat_g));
                if (p2.sugar_g !== null) setSugar(String(p2.sugar_g));
                hapticSelect();
                setAiMeta((m) => (m ? { ...m, prior: null } : m));
              }}
              className="col-span-2 text-left text-sm bg-accent-move/10 border border-accent-move/30 rounded-xl px-3 py-2.5 press"
            >
              {t("correction.chip", { kcal: String(aiMeta.prior.calories) })}{" "}
              <span className="underline">{t("correction.apply")}</span>
            </button>
          )}
          {/* guard(): anything typed while an estimate is in flight is the
              user's and the response leaves it alone. */}
          <Field label={t("food_sheet.name")}     value={name}     onChange={guard("name", setName)}         type="text" />
          <UnitInput
            label={kj ? `${t("food_sheet.calories")} (kJ)` : t("food_sheet.calories")}
            value={calories}
            onChange={guard("calories", setCalories)}
            toDisplay={(n) => (kj ? Math.round(kcalToKj(n)) : n)}
            fromDisplay={(n) => (kj ? kjToKcal(n) : n)}
            inputMode="numeric"
          />
          {premium ? (
            <>
              <Field label={t("food_sheet.protein")}  value={protein}  onChange={guard("protein", setProtein)}   type="number" step={0.1} />
              <Field label={t("food_sheet.carbs")}    value={carbs}    onChange={guard("carbs", setCarbs)}       type="number" step={0.1} />
              <Field label={t("food_sheet.fat")}      value={fat}      onChange={guard("fat", setFat)}           type="number" step={0.1} />
              <Field label={t("food_sheet.sugar")}    value={sugar}    onChange={guard("sugar", setSugar)}       type="number" step={0.1} />
              <Field label={t("macro.fiber")}         value={fiber}    onChange={guard("fiber", setFiber)}       type="number" step={0.1} />
            </>
          ) : (
            // The moved gate: every title stays, the values are Pro.
            <>
              <LockedField label={t("food_sheet.protein")} onUpgrade={() => setShowPaywall(true)} />
              <LockedField label={t("food_sheet.carbs")}   onUpgrade={() => setShowPaywall(true)} />
              <LockedField label={t("food_sheet.fat")}     onUpgrade={() => setShowPaywall(true)} />
              <LockedField label={t("food_sheet.sugar")}   onUpgrade={() => setShowPaywall(true)} />
              <LockedField label={t("macro.fiber")}        onUpgrade={() => setShowPaywall(true)} />
            </>
          )}
        </div>

        {premium && micros && Object.keys(micros).length > 0 && (
          <div className="mt-3">
            <MicrosList micros={micros} />
          </div>
        )}

        {err && <p className="text-danger text-sm mt-3">{err}</p>}

        {mode === "add" && (
          <label className="flex items-center gap-2 text-sm mt-4">
            <input
              type="checkbox"
              checked={saveAsMeal}
              onChange={(e) => setSaveAsMeal(e.target.checked)}
              className="h-5 w-5 accent-white"
            />
            <span>{t("saved.save_as_meal")}</span>
          </label>
        )}

        <div className="flex gap-2 mt-4 mb-2">
          <Button onClick={save} disabled={busy} className="flex-1">
            {busy ? t("food_sheet.saving") : t("food_sheet.save")}
          </Button>
          {/* No onDeleted means the opener has nowhere to apply a removal — show
              no button rather than one that does nothing. */}
          {mode === "edit" && onDeleted && (
            <Button variant="danger" onClick={del} disabled={busy}>
              {t("food_sheet.delete")}
            </Button>
          )}
        </div>
      {scannerOpen && (
        <BarcodeScannerSheet
          onMatch={(p, code) => {
            setScannerOpen(false);
            if (p) {
              setProduct(p);
              setScannedCode(p.code);
            } else if (code) {
              setScannedCode(code);
              // Fall through to AI estimator: focus the textarea, show a toast via err state.
              setErr(t("barcode.miss_use_ai"));
              setTimeout(() => descRef.current?.focus(), 0);
            }
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}
      {scanIntro && (
        <PremiumFeatureIntro
          onUnlock={() => { setScanIntro(false); setShowPaywall(true); }}
          onClose={() => setScanIntro(false)}
        />
      )}
      {showPaywall && <Paywall trigger="feature" onClose={() => setShowPaywall(false)} />}
    </SheetShell>
  );
}

function Field({ label, value, onChange, type, step }: { label: string; value: string; onChange: (v: string) => void; type: "text" | "number"; step?: number }) {
  return (
    <label className="block">
      <span className="text-sm text-fg-muted uppercase tracking-wider">{label}</span>
      <input
        type={type} step={step}
        value={value} onChange={(e) => onChange(e.target.value)}
        className="input mt-1"
        inputMode={type === "number" ? "decimal" : "text"}
      />
    </label>
  );
}
